import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import RMultilingual from '../Multilingual';
import Input from '~~elements/Input';
import Select from '~~elements/Select';
import Button from '~~elements/Button';
import Form from '~~elements/Form';
import toast from '~~elements/Toast';
import { RegistrySectionDiv } from '../../styled';
import { useRegistryState } from '../useRegistry';
import { parseBasicData, getLinkPrivacy } from '../service';
import { linkPrivacyList } from '../Const';

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  font-size: 0.9rem;
`;

const BasicSection = ({ onChange, fileChange, apiInProgress }) => {
  const { registryInfo } = useRegistryState();

  const [currentBasicData, setCurrentBasicData] = useState(parseBasicData(registryInfo));
  const [linkPrivacy, setLinkPrivacy] = useState('PUBLIC');

  const [contentRefresh, setContentRefresh] = useState(0);
  const [regulationFile, setRegulationFile] = useState(null);

  useEffect(() => {
    setCurrentBasicData(parseBasicData(registryInfo));
  }, [registryInfo]);

  useEffect(() => {
    const originBasicData = JSON.stringify(parseBasicData(registryInfo));
    if (originBasicData === JSON.stringify(currentBasicData)) {
      onChange(null);
      return;
    }

    onChange(currentBasicData);
    return;
  }, [currentBasicData, onChange, registryInfo]);

  useEffect(() => {
    setLinkPrivacy(getLinkPrivacy(registryInfo.is_public, registryInfo.entry_key));
  }, [registryInfo.entry_key, registryInfo.is_public]);

  const handleInputOnChange = (e) => {
    if (e.target.type === 'checkbox') {
      setCurrentBasicData({
        ...currentBasicData,
        [e.target.name]: e.target.checked,
      });
      return;
    }

    setCurrentBasicData({
      ...currentBasicData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePrivacyOnChange = (e) => {
    setLinkPrivacy(e.target.value);
    if (e.target.value === 'PUBLIC') {
      setCurrentBasicData({
        ...currentBasicData,
        is_public: true,
        entry_key: '',
      });
      return;
    }

    setCurrentBasicData({
      ...currentBasicData,
      is_public: false,
      entry_key: '',
    });
  };

  const removeRegulationFile = () => {
    setCurrentBasicData({
      ...currentBasicData,
      regulation_file_path: 'REMOVE',
      regulation_file_url: '',
    });
  };

  const handleFileOnChange = (e) => {
    e.preventDefault();

    const file = e.target.files[0];
    if (!file) {
      fileChange(null);
      setRegulationFile(null);
      return;
    }

    const { size: fileSize, type } = file;

    if (type !== 'application/pdf' || fileSize > 1024 * 1024 * 25) {
      toast(RMultilingual(`ERROR.CONTENT`), { status: 'error', second: 2 });
      setContentRefresh((pre) => pre + 1);
      fileChange(null);
      setRegulationFile(null);
      return;
    }

    fileChange(file);
    setRegulationFile(file);
    if (currentBasicData.regulation_file_path === 'REMOVE') {
      setCurrentBasicData({
        ...currentBasicData,
        regulation_file_path: registryInfo.regulation_file_path,
        regulation_file_url: '',
      });
    }
  };

  return (
    <RegistrySectionDiv>
      <Form onSubmit={(e) => e.preventDefault()}>
        <div className="box">
          {/* 報名期間 */}
          <label>{RMultilingual('DATE_RANGE')}</label>
          <div className="input-field --multi-input">
            <div className="input-box">
              <Input
                width="100%"
                name="start_at"
                placeholder={RMultilingual(`DATE_RANGE_HINT`)}
                value={currentBasicData.start_at}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
              {` ~ `}
              <Input
                width="100%"
                name="end_at"
                placeholder={RMultilingual(`DATE_RANGE_HINT`)}
                value={currentBasicData.end_at}
                onChange={handleInputOnChange}
                disabled={apiInProgress}
              />
            </div>
          </div>
          {/* 報名代碼 */}
          <label>{RMultilingual('CODE')}</label>
          <h3>{registryInfo.uniqid}</h3>
          {/* 報名連結 */}
          <label>{RMultilingual('LINK')}</label>
          <h3>
            <a
              href={`${process.env.REACT_APP_MAINPAGE_URL}/registry/${registryInfo.uniqid}`}
              target="_blank"
              rel="noopener noreferrer"
            >{`${process.env.REACT_APP_MAINPAGE_URL}/registry/${registryInfo.uniqid}`}</a>
          </h3>
          {/* 報名連結隱私 */}
          <label>
            {RMultilingual('LINK_PRIVACY.WORDING')}
            {linkPrivacy !== 'PUBLIC' && <i>{RMultilingual(`LINK_PRIVACY.${linkPrivacy}_HINT`)}</i>}
          </label>
          <div className="input-field">
            <Select value={linkPrivacy} onChange={handlePrivacyOnChange} disabled={apiInProgress}>
              {linkPrivacyList.map((privacy) => (
                <option key={privacy} value={privacy}>
                  {RMultilingual(`LINK_PRIVACY.${privacy}`)}
                </option>
              ))}
            </Select>
            {linkPrivacy === 'KEY_REQUIRED' && (
              <Fragment>
                <label>{RMultilingual(`LINK_PRIVACY.ENTRY_KEY`)}</label>
                <Input
                  width="5rem"
                  name="entry_key"
                  value={currentBasicData.entry_key}
                  disabled={apiInProgress}
                  onChange={handleInputOnChange}
                />
              </Fragment>
            )}
          </div>
          {/* 報名引文 */}
          <label>{RMultilingual('DESCRIPTION')}</label>
          <div className="input-field">
            <DescriptionTextarea
              name="description"
              value={currentBasicData.description}
              onChange={handleInputOnChange}
              disabled={apiInProgress}
            />
          </div>
          {/* 報名規章 */}
          <label>
            {RMultilingual('CONTENT')}
            <i className="info">{RMultilingual(`CONTENT_HINT`)}</i>
          </label>
          <label>
            {currentBasicData.regulation_file_url && !regulationFile && (
              <Fragment>
                <a href={currentBasicData.regulation_file_url} target="_blank" rel="noopener noreferrer">
                  {currentBasicData.regulation_file_url}
                </a>
                <Button width="unset" color="transparent" onClick={removeRegulationFile} type="button">
                  X
                </Button>
              </Fragment>
            )}
          </label>
          <div className="input-field">
            <input
              key={contentRefresh}
              type="file"
              accept="application/pdf"
              onChange={handleFileOnChange}
              disabled={apiInProgress}
            />
          </div>
          {/* 隊伍數上限 */}
          <label>{RMultilingual('TEAM_LIMIT')}</label>
          <div className="input-field">
            <Input
              width="10rem"
              name="team_limit"
              placeholder={RMultilingual(`TEAM_LIMIT`)}
              value={currentBasicData.team_limit}
              onChange={handleInputOnChange}
              disabled={apiInProgress}
            />
          </div>
          {/* 報隊需求 */}
          <label>{RMultilingual('TEAM_DESCRIPTION')}</label>
          <div className="input-field">
            <DescriptionTextarea
              name="team_description"
              value={currentBasicData.team_description}
              onChange={handleInputOnChange}
              disabled={apiInProgress}
            />
          </div>
        </div>
      </Form>
    </RegistrySectionDiv>
  );
};

export default BasicSection;
